/**
 * Android(Android Management API)。設定 → 企業への紐付け → ポリシー → 登録トークン。
 *
 * **並びがそのまま手順**になっている(上から順に埋めると端末が登録できる):
 *
 * 1. GCP の準備(`docs/mdm-android.md`)を済ませて、サービスアカウントの鍵と
 *    project_id を入れる
 * 2. 「同意画面の URL を作る」→ 開いた先で企業を作る → 戻ってきた
 *    `enterpriseToken` を貼って「紐付ける」(**やり直せない**)
 * 3. ポリシーをテンプレから選んで保存(紐付けの直後に `default` ができている)
 * 4. 登録トークンを発行して、出た QR を端末の初期設定で読ませる
 *
 * **1 回だけ見えるもの**: 同意画面の URL(トークン付き)と、登録トークンの値 / QR。
 * どちらもサーバーに残していないので、閉じたら作り直す。
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  QRCode,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { FormInstance } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useRef, useState } from "react";

import type { AndroidToken } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import {
  errorText,
  useAndroidConfig,
  useAndroidPolicies,
  useAndroidTokens,
  useInvalidateMdm,
  useMdmPerms,
} from "./hooks";

const { Paragraph, Text } = Typography;

const MASK = "•••設定済み";
const PERSONAL = [
  { value: "", label: "指定しない(フル管理)" },
  { value: "PERSONAL_USAGE_ALLOWED", label: "私用あり(仕事用プロファイル)" },
  { value: "PERSONAL_USAGE_DISALLOWED", label: "私用なし(会社所有)" },
];
const DURATIONS = [
  { value: 3600, label: "1 時間" },
  { value: 86400, label: "1 日" },
  { value: 7 * 86400, label: "7 日" },
  { value: 30 * 86400, label: "30 日" },
];

type ConfigForm = {
  sa_key?: string;
  project_id?: string;
  callback_url?: string;
  enterprise?: string;
};

/** サーバーの設定値をフォームの形にする。 */
function configValues(value: Record<string, unknown> | undefined): Required<ConfigForm> {
  const v = value ?? {};
  return {
    sa_key: v.sa_key ? String(v.sa_key) : "",
    project_id: String(v.project_id ?? ""),
    callback_url: String(v.callback_url ?? ""),
    enterprise: String(v.enterprise ?? ""),
  };
}

/** サーバーの設定値でフォームを埋める。 */
function fillConfig(form: FormInstance<ConfigForm>, value: Record<string, unknown> | undefined) {
  form.setFieldsValue(configValues(value));
}

/** 設定。SA の鍵は伏字で返るので、**空のまま保存しても消えない**。 */
function ConfigCard() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = useInvalidateMdm();
  const query = useAndroidConfig();
  const [form] = Form.useForm<ConfigForm>();
  // 人が編集した欄。`isFieldTouched` は `setFieldsValue` でも立つ(rc-field-form 2.7)ので
  // 使わず、`onValuesChange` で自前に持つ(MOB2 fix45 中1)
  const edited = useRef(new Set<keyof ConfigForm>());

  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.saveAndroidConfig(data),
    onSuccess: (res) => {
      void message.success("保存した(監査ログに記録した)");
      // 保存した値はサーバーの側が正(鍵は伏字で返る)。**PUT の応答**をキャッシュとフォームに
      // 入れて終える。待ち合わせない refetch → fillConfig は、取り直し中に打った値を消していた
      // (MOB2 fix45 中2)。他の節(紐付け・ポリシー)は invalidate で追いつく
      qc.setQueryData(["mdm", "android", "config"], res);
      edited.current.clear();
      fillConfig(form, res.value);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const test = useMutation({
    mutationFn: api.testAndroid,
    onSuccess: (r) =>
      r.ok
        ? void message.success(`繋がった: ${r.display_name || r.enterprise}`)
        : void message.error(r.error || "繋がらなかった"),
    onError: (e) => void message.error(errorText(e)),
  });

  // 前に見た紐付けの状態(紐付けが変わったかを見る。fix5 低 4)
  const lastBound = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const data = query.data;
    // 取れていない間はフォームに書かない(空文字で埋めると全欄が「触った」扱いになり、
    // 後から届くサーバー値を拒んでいた。MOB2 fix45 中1)
    if (data === undefined) return;
    const boundChanged = lastBound.current !== undefined && lastBound.current !== data.bound;
    lastBound.current = data.bound;
    // 人が先に打ち込んだ値を、遅れて返った GET で上書きしない(fix4 低 3)。**欄ごとに**見る
    const fresh = configValues(data.value);
    const patch: ConfigForm = {};
    for (const name of Object.keys(fresh) as (keyof ConfigForm)[]) {
      // ただし enterprise は**紐付けが変わったら**触っていても取り直す(fix5 低 4)。
      // 「紐付けを外す」の後に古い値が残ると、欄の disabled が外れたまま保存で
      // 外したはずの企業に紐付けが戻った
      if (!edited.current.has(name) || (name === "enterprise" && boundChanged)) {
        patch[name] = fresh[name];
        if (name === "enterprise" && boundChanged) edited.current.delete(name);
      }
    }
    form.setFieldsValue(patch);
  }, [query.data, form]);

  return (
    <Card size="small" title="設定(GCP のサービスアカウント)" data-testid="android-config">
      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="設定を取得できなかった"
          description={errorText(query.error)}
        />
      )}
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="紐付け">
          {query.data?.bound ? (
            <Tag color="green">済({query.data.enterprise})</Tag>
          ) : (
            <Tag color="orange">まだ(下の 2 段で紐付ける)</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="最終更新">{jst(query.data?.updated_at ?? null)}</Descriptions.Item>
      </Descriptions>
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate({ ...v })}
        onValuesChange={(changed: Partial<ConfigForm>) => {
          for (const k of Object.keys(changed) as (keyof ConfigForm)[]) edited.current.add(k);
        }}
        disabled={save.isPending}
      >
        <Form.Item
          name="sa_key"
          label="sa_key(サービスアカウントの JSON 鍵)"
          extra={`${MASK} のままにすると消えない。差し替えるときだけ貼り直す`}
        >
          <Input.TextArea rows={4} autoComplete="off" />
        </Form.Item>
        <Form.Item name="project_id" label="project_id" extra="GCP プロジェクトの ID">
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item
          name="callback_url"
          label="callback_url"
          extra="同意画面から戻ってくる先。空でも紐付けはできる(戻り先の URL から enterpriseToken を手で写す)"
        >
          <Input maxLength={2000} />
        </Form.Item>
        <Form.Item
          name="enterprise"
          label="enterprise(すでにある企業に手で合わせるとき)"
          extra={
            query.data?.bound
              ? `いまは ${query.data.enterprise}。差し替えはできない(先に下の「紐付けを外す」)`
              : "`enterprises/LC0xxxxxxx`。別の環境で作った企業をそのまま使うときだけ入れる"
          }
        >
          <Input
            maxLength={120}
            placeholder="enterprises/LC0xxxxxxx"
            disabled={query.data?.bound}
            data-testid="android-enterprise"
          />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            保存
          </Button>
          <Button onClick={() => test.mutate()} loading={test.isPending}>
            接続テスト
          </Button>
        </Space>
      </Form>
    </Card>
  );
}

/** 紐付け(2 段)。**やり直せない**ので、すでに紐付いていたら出さない。 */
function BindCard({ bound, enterprise }: { bound: boolean; enterprise: string }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const [form] = Form.useForm<{
    enterprise_token?: string;
    signup_url_name?: string;
    display_name?: string;
  }>();
  const [url, setUrl] = useState("");

  const signup = useMutation({
    mutationFn: api.androidSignupUrl,
    onSuccess: (r) => {
      setUrl(r.url);
      form.setFieldsValue({ signup_url_name: r.name });
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const clear = useMutation({
    mutationFn: api.clearAndroidEnterprise,
    onSuccess: () => {
      void message.success("紐付けを外した(監査ログに記録した)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const bind = useMutation({
    mutationFn: (v: {
      enterprise_token: string;
      signup_url_name: string;
      display_name?: string;
    }) => api.bindAndroidEnterprise(v),
    onSuccess: (r) => {
      void message.success(`紐付けた: ${r.enterprise}`);
      setUrl("");
      form.resetFields();
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (bound) {
    return (
      <Card size="small" title="企業への紐付け">
        <Alert
          type="success"
          showIcon
          message={`この環境は ${enterprise} に紐付いている`}
          description="紐付けの差し替えはできない(いま管理している端末が全部「知らない企業の端末」になる)。別の企業に移すときは、ここで紐付けを外してから設定の enterprise を入れ直す。外しているあいだ、端末にコマンドは送れない。"
        />
        <Popconfirm
          title="紐付けを外す"
          description={`${enterprise} を設定から外す(Google 側の企業と端末の履歴は残る)。外すと端末にコマンドを送れなくなる。`}
          okText="外す"
          okButtonProps={{ danger: true }}
          cancelText="やめる"
          onConfirm={() => clear.mutate()}
        >
          <Button
            danger
            style={{ marginTop: 12 }}
            loading={clear.isPending}
            data-testid="android-unbind"
          >
            紐付けを外す
          </Button>
        </Popconfirm>
      </Card>
    );
  }

  return (
    <Card size="small" title="企業への紐付け(2 段)" data-testid="android-bind">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Flex gap={8} align="center" wrap>
          <Button onClick={() => signup.mutate()} loading={signup.isPending}>
            1. 同意画面の URL を作る
          </Button>
          {url && (
            <Text>
              <a href={url} target="_blank" rel="noreferrer">
                同意画面を開く
              </a>{" "}
              <Text type="secondary" style={{ fontSize: 12 }}>
                (この URL は 1 回だけ。残していないので閉じたら作り直す)
              </Text>
            </Text>
          )}
        </Flex>
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) =>
            bind.mutate({
              enterprise_token: v.enterprise_token ?? "",
              signup_url_name: v.signup_url_name ?? "",
              display_name: v.display_name,
            })
          }
          disabled={bind.isPending}
        >
          <Form.Item
            name="enterprise_token"
            label="2. enterpriseToken(同意画面から戻ってきた URL のクエリ)"
            rules={[{ required: true, message: "enterpriseToken を貼る" }]}
          >
            <Input maxLength={2000} />
          </Form.Item>
          <Form.Item
            name="signup_url_name"
            label="signupUrlName(1 で作った名前。自動で入る)"
            rules={[{ required: true, message: "先に 1 を押す" }]}
          >
            <Input maxLength={300} />
          </Form.Item>
          <Form.Item name="display_name" label="企業の表示名(任意)">
            <Input maxLength={200} />
          </Form.Item>
          <Popconfirm
            title="この GCP プロジェクトを企業に紐付ける"
            description="やり直せない。紐付けると既定ポリシー default も作る"
            okText="紐付ける"
            cancelText="やめる"
            onConfirm={() => form.submit()}
          >
            <Button type="primary" danger loading={bind.isPending}>
              紐付ける
            </Button>
          </Popconfirm>
        </Form>
      </Space>
    </Card>
  );
}

/** ポリシー。テンプレを選んで JSON を直し、保存で `policies.patch`(先に Google へ配る)。 */
function PolicyCard({ bound }: { bound: boolean }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const query = useAndroidPolicies();
  const { canAdmin } = useMdmPerms();
  const [form] = Form.useForm<{
    policy_id?: string;
    name?: string;
    template?: string;
    body?: string;
  }>();

  const save = useMutation({
    mutationFn: (v: { policy_id: string; name: string; template: string; body: string }) => {
      let body: Record<string, unknown>;
      try {
        body = JSON.parse(v.body) as Record<string, unknown>;
      } catch {
        throw new Error("JSON として読めない");
      }
      return api.saveAndroidPolicy(v.policy_id, {
        name: v.name,
        template: v.template,
        body,
      });
    },
    onSuccess: (r) => {
      void message.success(`${r.policy_id} を配った`);
      invalidate();
      void query.refetch();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const templates = query.data?.templates ?? [];
  const rows = query.data?.items ?? [];

  const pickTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (t) form.setFieldsValue({ template: id, body: JSON.stringify(t.body, null, 2) });
  };

  const columns: ColumnsType<(typeof rows)[number]> = [
    { title: "policy_id", dataIndex: "policy_id", width: 180 },
    { title: "名前", dataIndex: "name", width: 180, },
    { title: "テンプレ", dataIndex: "template", width: 160 },
    { title: "更新者", dataIndex: "updated_by", width: 160, },
    {
      title: "更新",
      dataIndex: "updated_at",
      width: 180,
      render: (v: string | null) => jst(v),
    },
    {
      title: "",
      key: "edit",
      width: 90,
      render: (_: unknown, row) => (
        <Button
          size="small"
          onClick={() =>
            form.setFieldsValue({
              policy_id: row.policy_id,
              name: row.name,
              template: row.template,
              body: JSON.stringify(row.body, null, 2),
            })
          }
        >
          編集
        </Button>
      ),
    },
  ];

  return (
    <Card size="small" title="ポリシー" data-testid="android-policies">
      {!bound && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="先に企業へ紐付ける(保存は Google に配ってから控える)"
        />
      )}
      <Table
        scroll={{ x: "max-content" }}
        rowKey="policy_id"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        style={{ marginBottom: 12 }}
        locale={{ emptyText: "まだ無い(紐付けると default ができる)" }}
      />
      {canAdmin && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ policy_id: "default" }}
          onFinish={(v) =>
            save.mutate({
              policy_id: v.policy_id ?? "",
              name: v.name ?? "",
              template: v.template ?? "",
              body: v.body ?? "{}",
            })
          }
          disabled={save.isPending || !bound}
        >
          <Flex gap={8} wrap>
            <Form.Item
              name="policy_id"
              label="policy_id"
              rules={[{ required: true, message: "policy_id を入れる" }]}
            >
              <Input style={{ width: 200 }} maxLength={60} />
            </Form.Item>
            <Form.Item name="name" label="名前(控え)">
              <Input style={{ width: 220 }} maxLength={200} />
            </Form.Item>
            <Form.Item label="テンプレから作る">
              <Select
                style={{ width: 260 }}
                placeholder="選ぶと下の JSON が入れ替わる"
                onChange={pickTemplate}
                options={templates.map((t) => ({ value: t.id, label: t.label }))}
              />
            </Form.Item>
          </Flex>
          <Form.Item name="template" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="body"
            label="ポリシー(AMAPI の JSON)"
            rules={[{ required: true, message: "JSON を入れる" }]}
            extra="保存すると Google に配ってから控えを更新する(配れなければ控えも残らない)"
          >
            <Input.TextArea rows={12} style={{ fontFamily: "monospace", fontSize: 12 }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={save.isPending} disabled={!bound}>
            保存して配る
          </Button>
        </Form>
      )}
      {templates.length > 0 && (
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          {templates.map((t) => `${t.label}: ${t.hint}`).join(" / ")}
        </Paragraph>
      )}
    </Card>
  );
}

/** 登録トークン。**値と QR は発行の戻りだけ**なので、出たその場で控えさせる。 */
function TokenCard({ bound }: { bound: boolean }) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const policies = useAndroidPolicies();
  const query = useAndroidTokens();
  const { canWrite } = useMdmPerms();
  const [form] = Form.useForm();
  const [issued, setIssued] = useState<AndroidToken | null>(null);
  /** QR に入れる本文。**1 か所で決める**(表示と通しテストが同じものを見る)。 */
  const qrValue = issued ? issued.qr_code || issued.value || "" : "";

  const create = useMutation({
    mutationFn: (v: Record<string, unknown>) => api.createAndroidToken(v),
    onSuccess: (r) => {
      setIssued(r);
      invalidate();
      void query.refetch();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const revoke = useMutation({
    mutationFn: (id: number) => api.revokeAndroidToken(id),
    onSuccess: () => {
      void message.success("取り消した(もう登録には使えない)");
      invalidate();
      void query.refetch();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<AndroidToken> = [
    { title: "ポリシー", dataIndex: "policy_id", width: 180 },
    {
      title: "利用者",
      dataIndex: "employee_email",
      width: 240,
      render: (v: string) => v || "—",
    },
    {
      title: "私用",
      dataIndex: "allow_personal_usage",
      width: 240,
      render: (v: string) => PERSONAL.find((x) => x.value === v)?.label ?? v,
    },
    {
      title: "期限",
      dataIndex: "expires_at",
      width: 180,
      render: (v: string | null, row) =>
        row.expired ? <Tag color="default">切れた</Tag> : jst(v),
    },
    { title: "発行者", dataIndex: "created_by", width: 160, },
    {
      title: "",
      key: "revoke",
      width: 110,
      render: (_: unknown, row) =>
        canWrite && !row.expired ? (
          <Popconfirm
            title="この登録トークンを取り消す"
            okText="取り消す"
            cancelText="やめる"
            onConfirm={() => revoke.mutate(row.id)}
          >
            <Button size="small" danger>
              取り消す
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Card size="small" title="登録トークン(QR)" data-testid="android-tokens">
      {canWrite && (
        <Form
          form={form}
          layout="inline"
          initialValues={{ policy_id: "default", duration: 3600, one_time_only: true }}
          onFinish={(v) => create.mutate(v)}
          disabled={create.isPending || !bound}
          style={{ marginBottom: 12, rowGap: 8 }}
        >
          <Form.Item name="policy_id" label="ポリシー">
            <Select
              style={{ width: 180 }}
              options={(policies.data?.items ?? []).map((p) => ({
                value: p.policy_id,
                label: p.policy_id,
              }))}
            />
          </Form.Item>
          <Form.Item name="duration" label="有効期限">
            <Select style={{ width: 140 }} options={DURATIONS} />
          </Form.Item>
          <Form.Item name="allow_personal_usage" label="私用">
            <Select style={{ width: 240 }} options={PERSONAL} allowClear />
          </Form.Item>
          <Form.Item name="employee_email" label="利用者(任意)">
            <Input style={{ width: 240 }} maxLength={320} placeholder="user@example.com" />
          </Form.Item>
          <Form.Item name="one_time_only" label="1 台だけ" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={create.isPending} disabled={!bound}>
              発行する
            </Button>
          </Form.Item>
        </Form>
      )}
      <Table<AndroidToken>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        pagination={false}
        scroll={{ x: "max-content", y: 320 }}
        locale={{ emptyText: "まだ発行していない" }}
      />
      <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        トークンの値と QR は発行した直後の 1 回だけ見える(保存していない)。閉じたら作り直す。
      </Paragraph>

      <Modal
        open={!!issued}
        title={issued?.setup.work_profile ? "この QR を仕事用プロファイルで読ませる"
                                          : "この QR を端末で読ませる"}
        footer={null}
        onCancel={() => {
          setIssued(null);
          form.resetFields();
        }}
      >
        {issued && (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Alert
              type="warning"
              showIcon
              message="見えるのは今だけ(サーバーに残していない)"
              description="期限が切れたら作り直す。"
            />
            {/* 端末側の手順は**私用ありだけ初期化が要らない**(低 14)。文言は
                サーバー(`setup_guide`)が出し分ける — 仕事用プロファイルの
                端末に「6 回タップして初期化」と書くと私用領域ごと消える */}
            <Alert
              type={issued.setup.work_profile ? "success" : "info"}
              showIcon
              data-testid="android-setup"
              message={issued.setup.title}
              description={
                <ol style={{ margin: 0, paddingInlineStart: 20 }}>
                  {issued.setup.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              }
            />
            {/* `data-qr-value` は通しテストの足場(自己レビュー 10)。canvas の
                中身は読めないので、QR に渡した本文をここに出す。値はこの直下に
                そのまま表示している登録トークンなので、新たに晒すものは無い。
                **QR と同じ 1 つの変数**から出す(2 巡目 5。別々の式にすると
                片方だけ壊れたときに通しテストが気づかない) */}
            <Flex justify="center" data-testid="android-qr" data-qr-value={qrValue}>
              <QRCode value={qrValue} size={240} errorLevel="M" />
            </Flex>
            <Text type="secondary" style={{ fontSize: 12 }}>
              期限 {jst(issued.expires_at)} / ポリシー {issued.policy_id}
            </Text>
            <Text code copyable={{ text: issued.value ?? "" }}>
              {issued.value}
            </Text>
          </Space>
        )}
      </Modal>
    </Card>
  );
}

export function AndroidPage() {
  const api = useApi();
  const { canAdmin } = useMdmPerms();
  const config = useAndroidConfig(canAdmin);
  const invalidate = useInvalidateMdm();

  const sync = useMutation({
    mutationFn: api.syncAndroid,
    onSuccess: (r) => {
      if (r.skipped) {
        void message.warning(
          r.skipped === "quota"
            ? "Google のクォータを超えた(5 分後のジョブに任せる)"
            : r.skipped === "not_configured"
              ? "まだ設定・紐付けが済んでいない"
              : `同期できなかった: ${r.error}`,
        );
      } else {
        const got = `同期した(${r.devices} 台 / 新規 ${r.created} / 決着 ${r.resolved})`;
        // 一覧が途中までの回は「消えた端末」の判定をしていない。成功と言い切らない
        if (r.partial) {
          void message.warning(
            `${got}。ただし一覧は途中まで${r.truncated ? "(台数が上限を超えた)" : ""}` +
              " — 初期化・所有権返却の決着は次の同期に回した",
          );
        } else {
          void message.success(got);
        }
      }
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const bound = !!config.data?.bound;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
          GCP の準備 → 設定 → 紐付け → ポリシー → 登録トークンの順。詳しい手順は
          docs/mdm-android.md。台帳は 5 分ごとに同期する。
        </Paragraph>
        <Button onClick={() => sync.mutate()} loading={sync.isPending}>
          いま同期する
        </Button>
      </Flex>
      {!canAdmin && (
        <Alert
          type="info"
          showIcon
          message="設定と紐付けは mdm:admin だけ"
          description="登録トークンの発行と同期は mdm:operator でできる。"
        />
      )}
      {canAdmin && <ConfigCard />}
      {canAdmin && (
        <BindCard bound={bound} enterprise={config.data?.enterprise ?? ""} />
      )}
      <PolicyCard bound={bound || !canAdmin} />
      <TokenCard bound={bound || !canAdmin} />
    </Space>
  );
}
